import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { ArrowRight, ArrowDown, Check } from "lucide-react";

const Hero = () => {
  const { t } = useLanguage();

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-28 px-5 sm:px-8"
    >
      {/* Background */}
      <div className="absolute inset-0 dots-bg opacity-60 pointer-events-none" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-accent/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* Left: text */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-7 animate-fade-up" style={{ animationDelay: "0.1s" }}>
            <span className="w-12 h-px bg-brand" aria-hidden />
            <span className="text-[11px] sm:text-xs tracking-[0.25em] font-bold text-brand">
              {t.hero.eyebrow}
            </span>
          </div>

          <h1 className="font-display font-light text-brand-ink" data-testid="hero-title">
            <span className="block text-[2.75rem] sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tighter animate-fade-up" style={{ animationDelay: "0.2s" }}>
              {t.hero.titleLine1}
            </span>
            <span className="block text-[2.75rem] sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tighter text-brand font-script italic animate-fade-up" style={{ animationDelay: "0.35s" }}>
              {t.hero.titleLine2}
            </span>
            <span className="block text-[2.75rem] sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tighter animate-fade-up" style={{ animationDelay: "0.5s" }}>
              {t.hero.titleLine3}
            </span>
          </h1>

          <p
            className="mt-8 max-w-xl text-base sm:text-lg text-brand-muted leading-relaxed animate-fade-up"
            style={{ animationDelay: "0.65s" }}
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "0.8s" }}>
            <a
              href="#contact"
              onClick={scrollTo("contact")}
              data-testid="hero-primary-cta"
              className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-sm sm:text-base font-semibold shadow-lg hover:bg-brand-hover hover:-translate-y-1 transition-all duration-300 shine-hover"
            >
              {t.hero.primaryCta}
              <ArrowRight size={18} strokeWidth={2} />
            </a>
            <a
              href="#services"
              onClick={scrollTo("services")}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white text-brand border border-brand-line px-7 py-4 text-sm sm:text-base font-semibold hover:bg-brand-surface hover:-translate-y-1 transition-all duration-300"
            >
              {t.hero.secondaryCta}
            </a>
          </div>

          {/* Honest tags */}
          <div className="mt-10 flex flex-wrap items-center gap-2 animate-fade-up" style={{ animationDelay: "0.95s" }}>
            {t.hero.tagsBelow.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-2 rounded-full bg-brand-surface border border-brand-line/70 px-4 py-1.5 text-xs sm:text-sm font-medium text-brand"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-brand" />
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Right: pillars composition (no fake numbers) */}
        <div className="lg:col-span-5 relative hidden md:block animate-fade-up" style={{ animationDelay: "0.55s" }}>
          {/* Main pillars card */}
          <div className="relative rounded-[2rem] bg-white border border-brand-line/80 shadow-2xl p-8 z-20">
            <div className="text-[10px] tracking-[0.3em] font-bold text-brand mb-5 flex items-center gap-2">
              <span className="w-6 h-px bg-brand" />
              {t.hero.pillarsTitle.toUpperCase()}
            </div>
            <ul className="space-y-5">
              {t.hero.pillars.map((p, i) => (
                <li key={i} className="flex items-start gap-4 group">
                  <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-brand-surface border border-brand-line flex items-center justify-center mt-0.5 group-hover:bg-brand transition-colors duration-300">
                    <Check
                      size={16}
                      strokeWidth={2}
                      className="text-brand group-hover:text-white transition-colors duration-300"
                    />
                  </span>
                  <div>
                    <div className="font-display text-lg font-medium text-brand-ink tracking-tight">
                      {p.label}
                    </div>
                    <div className="text-sm text-brand-muted leading-snug">
                      {p.desc}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Decorative offset card behind */}
          <div
            className="absolute inset-0 rounded-[2rem] bg-brand/5 -z-10 translate-x-4 translate-y-4"
            aria-hidden
          />
        </div>
      </div>

      {/* Scroll hint */}
      <div className="relative max-w-7xl mx-auto mt-16 flex items-center gap-3 text-brand-muted animate-float-slow">
        <ArrowDown size={16} strokeWidth={1.5} />
        <span className="text-[11px] tracking-[0.3em] font-semibold uppercase">
          {t.hero.scrollHint}
        </span>
      </div>
    </section>
  );
};

export default Hero;
